import { useRef, useState } from 'react';
import { Upload } from 'lucide-react';
import DocumentConfirmSheet from './DocumentConfirmSheet.jsx';
import { classLabel } from '@/lib/dropKinds.js';
import { DOCS_NOT_CONNECTED, DOCUMENT_ERROR_WORDS, FILED_REREADING, word } from './words.js';

// THE DROP ZONE (stage D3; paperwork-1 L1b-B1: repointed to the deal's document door, D7). A drop or a pick holds each file
// on a row and opens the confirm sheet (DocumentConfirmSheet) for it, one at a time: the broker says what it is and which
// term it goes on, and the sheet files it through fileDealDocuments on that term's deal. Nothing is uploaded before the
// broker answers (the server's AI guess this zone used to show was never built on main; the door files by the stated kind).
// The answer renders on the row AS THE SERVER ANSWERED IT (the stored row's derived name and kind), then the page re-reads
// the account. A row the broker cancelled says nothing was filed and offers the sheet again. Progress and errors render on
// the row, in place: nothing goes to the console, nothing is a toast beside the row.

export const DROP_COPY = 'Drop any file here. TRACON asks what it is and which term it goes on, then files it on that policy.';
export const PHASE_WORDS = Object.freeze({ waiting: 'waiting for your answer', filed: FILED_REREADING, held: 'not filed; nothing was stored', error: 'not filed' });

// What the door answered, as words on the row: the derived name, the kind and the term it was filed on.
function FiledLine({ res, terms }) {
  const d = res?.document || null;
  const t = d ? terms.find((x) => x.id === d.policy_id) : null;
  return (
    <span className="flex basis-full flex-wrap gap-x-3 gap-y-0.5 text-xs" data-filed>
      <span className="text-text">Filed as {d ? d.name : res?.name} ({d ? classLabel(d.class).toLowerCase() : 'signed application'}){t ? ` on ${t.carrier?.name || 'the term'} ${t.policy_number || ''}`.trimEnd() : ''}</span>
    </span>
  );
}

export default function DropZone({ account, terms = [], interests = [], currentTermId = null, onSaved, docsConnected = null }) {
  const [items, setItems] = useState([]);
  const [over, setOver] = useState(false);
  const [sheetKey, setSheetKey] = useState(null);
  const input = useRef(null);
    const patch = (key, p) => setItems((list) => list.map((x) => (x.key === key ? { ...x, ...(typeof p === 'function' ? p(x) : p) } : x)));

  const take = (list) => {
    const files = Array.from(list || []);
    if (!files.length) return;
    const fresh = files.map((f, i) => ({ key: `${f.name}-${f.size}-${Date.now()}-${i}`, name: f.name, size: f.size, file: f, phase: 'waiting', error: null, confirmed: null }));
    setItems((l) => [...l, ...fresh]);
    setSheetKey((k) => k || fresh[0].key);
  };

  // The next row waiting for an answer, read from the rendered list (an
  // upload landing later opens the sheet itself when none is open).
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const nextWaiting = (except) => itemsRef.current.find((x) => x.phase === 'waiting' && x.key !== except)?.key || null;
  const onCancel = () => {
    const key = sheetKey;
    patch(key, { phase: 'held' });
    setSheetKey(nextWaiting(key));
  };
  const onConfirmed = (res) => {
    const key = sheetKey;
    patch(key, { phase: 'filed', confirmed: res });
    setSheetKey(nextWaiting(key));
    onSaved?.();
  };
  const sheetItem = items.find((x) => x.key === sheetKey) || null;

  // The docs read answered the server's unbuilt-route 404 (v3-docs not merged): say so quietly, offer no drop.
  if (docsConnected === false) {
    return <section className="rounded-card border border-dashed border-border bg-surface px-4 py-5 text-center text-sm text-text-dim" data-drop-zone data-not-connected="docs">{DOCS_NOT_CONNECTED}</section>;
  }

  return (
    <section
      className={`rounded-card border border-dashed px-4 py-5 text-center ${over ? 'border-accent bg-accent-soft' : 'border-border-strong bg-surface'}`}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); take(e.dataTransfer?.files); }}
      data-drop-zone
    >
      <Upload size={18} strokeWidth={1.5} className="mx-auto mb-2 text-text-dim" />
      <p className="text-sm text-text">{DROP_COPY}</p>
      <button type="button" className="mt-1 inline-flex min-h-[36px] items-center text-xs text-text-muted underline-offset-2 hover:text-text hover:underline" onClick={() => input.current?.click()}>or choose a file</button>
      <input ref={input} type="file" multiple className="hidden" onChange={(e) => { take(e.target.files); e.target.value = ''; }} aria-label="Choose a file" />
      {items.length ? (
        <ul className="mt-3 space-y-1 text-left text-sm" data-dropped-files>
          {items.map((f) => (
            <li key={f.key} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5" data-dropped-file={f.key} data-phase={f.phase}>
              <span className="font-medium text-text">{f.name}</span>
              <span className="font-mono text-xs text-text-dim">{Math.round(f.size / 1024)} KB</span>
              {f.phase === 'error' ? <span className="text-xs text-danger" data-upload-error={f.error}>{word(DOCUMENT_ERROR_WORDS, f.error)}</span> : <span className="text-xs text-text-muted" data-phase-word>{PHASE_WORDS[f.phase]}</span>}
              {f.phase === 'held' ? <button type="button" className="inline-flex min-h-[36px] items-center text-xs text-accent underline-offset-2 hover:underline" onClick={() => { patch(f.key, { phase: 'waiting' }); setSheetKey(f.key); }}>File {f.name}</button> : null}
              {f.phase === 'filed' ? <FiledLine res={f.confirmed} terms={terms} /> : null}
            </li>
          ))}
        </ul>
      ) : null}
      {sheetItem ? <DocumentConfirmSheet key={sheetItem.key} item={sheetItem} account={account} terms={terms} interests={interests} currentTermId={currentTermId} onCancel={onCancel} onConfirmed={onConfirmed} /> : null}
    </section>
  );
}
